import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import ConfirmDialog from "../components/common/dialogs/ConfirmDialog";

/**
 * App-wide confirmation dialog.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Confirm Upload", content: "...", confirmLabel: "Upload" }))) return;
 *
 * Resolves true on confirm, false on cancel / close.
 */
const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [options, setOptions] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback(
    (opts = {}) =>
      new Promise((resolve) => {
        resolver.current = resolve;
        setOptions(opts);
      }),
    [],
  );

  const settle = (value) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={Boolean(options)}
        onClose={() => settle(false)}
        onConfirm={() => settle(true)}
        title={options?.title ?? "Confirm"}
        content={options?.content ?? "Are you sure?"}
        confirmLabel={options?.confirmLabel ?? "Yes"}
        confirmColor={options?.confirmColor ?? "primary"}
      />
    </ConfirmContext.Provider>
  );
};

// Outside the provider (e.g. an isolated test render) fall back to the
// browser dialog rather than silently skipping the confirmation.
const fallbackConfirm = async (opts = {}) =>
  window.confirm(opts.content || opts.title || "Are you sure?");

export const useConfirm = () => useContext(ConfirmContext) ?? fallbackConfirm;
